import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/dealership/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/dealership/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatTile, PillTabs, StatusPill, EmptyState, LivePill } from "@/components/lux";
import { money, STATUS_TONE } from "@/dealership/lib/desk";
import { Calculator, Plus, FileSpreadsheet, CheckCircle2, Presentation, DollarSign, Settings2 } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "worksheet", label: "Working" },
  { key: "presented", label: "Presented" },
  { key: "accepted", label: "Accepted" },
  { key: "converted", label: "Converted" },
];

export default function Desking() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const internal = user?.role === "admin" || user?.role === "staff";
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [leadId, setLeadId] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const { data: rows = [], isLoading } = useQuery({ queryKey: ["worksheets"], queryFn: async () => (await api.get("/desking/worksheets")).data });
  const { data: inventory = [] } = useQuery({ queryKey: ["inventory"], queryFn: async () => (await api.get("/inventory")).data, enabled: open });
  const { data: leads = [] } = useQuery({ queryKey: ["leads"], queryFn: async () => (await api.get("/leads")).data, enabled: open });

  const create = async () => {
    setCreating(true);
    try {
      const { data: draft } = await api.get("/desking/prefill", { params: { vehicle_id: vehicleId, lead_id: leadId } });
      const { totals, id, created_at, updated_at, created_by, converted_deal_id, ...payload } = draft;
      payload.name = name || (draft.vehicle_desc ? draft.vehicle_desc : "New worksheet");
      const { data } = await api.post("/desking/worksheets", payload);
      qc.invalidateQueries({ queryKey: ["worksheets"] });
      setOpen(false);
      setVehicleId(""); setLeadId(""); setName("");
      navigate(`/dealership/desking/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start a worksheet");
    } finally { setCreating(false); }
  };

  const shown = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const count = (s) => rows.filter((r) => r.status === s).length;
  const grossPipeline = rows.filter((r) => ["presented", "accepted"].includes(r.status)).reduce((s, r) => s + (r.totals?.total_gross || 0), 0);

  return (
    <div className="p-6 sm:p-8" data-testid="desking-page">
      <PageHeader
        title="Desking"
        subtitle={`${rows.length} worksheet${rows.length === 1 ? "" : "s"} · structure every number, present clean payment options`}
        live={<LivePill />}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/dealership/desking-settings")} data-testid="desking-settings-btn">
              <Settings2 className="h-4 w-4 mr-2" />Settings
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="new-worksheet-button">
              <Plus className="h-4 w-4 mr-2" />New worksheet
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6 stagger">
        <StatTile label="Working" value={count("worksheet")} hint="Being structured" icon={Calculator} tone="blue" testid="desk-kpi-working" />
        <StatTile label="Presented" value={count("presented")} hint="Shown to a customer" icon={Presentation} tone="amber" testid="desk-kpi-presented" />
        <StatTile label="Accepted" value={count("accepted")} hint="Ready to convert to a deal" icon={CheckCircle2} tone="green" testid="desk-kpi-accepted" />
        {internal
          ? <StatTile label="Gross In Pipeline" value={money(grossPipeline)} gold hint="Presented + accepted worksheets" icon={DollarSign} tone="gold" testid="desk-kpi-gross" />
          : <StatTile label="Converted" value={count("converted")} hint="Became a deal" icon={FileSpreadsheet} tone="gold" testid="desk-kpi-converted" />}
      </div>

      <div className="mb-5">
        <PillTabs tabs={FILTERS.map((f) => ({ ...f, count: f.key === "all" ? rows.length : count(f.key), testid: `desk-filter-${f.key}` }))} value={filter} onChange={setFilter} size="sm" testid="desk-filters" />
      </div>

      {isLoading ? (
        <div className="border border-border bg-card p-12 text-center text-muted-foreground">Loading…</div>
      ) : shown.length === 0 ? (
        <div className="border border-border bg-card">
          <EmptyState
            icon={Calculator}
            title="No worksheets yet"
            hint="Start one from a vehicle, a lead, or from scratch — then structure price, trade, fees, products and payments."
            action={<Button onClick={() => setOpen(true)}>New worksheet</Button>}
          />
        </div>
      ) : (
        <div className="border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                {["Worksheet", "Customer", "Vehicle", "Structure", "Payment", ...(internal ? ["Gross"] : []), "Status", "Updated"].map((h, i) => (
                  <th key={h} className={`px-4 py-3.5 label-mono font-normal ${i > 2 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const t = r.totals || {};
                return (
                  <tr key={r.id} onClick={() => navigate(`/dealership/desking/${r.id}`)} data-testid={`worksheet-row-${r.id}`}
                    className="border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] cursor-pointer transition-colors">
                    <td className="px-4 py-4 font-heading text-base">{r.name}</td>
                    <td className="px-4 py-4">{r.customer_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-4 text-muted-foreground">{r.vehicle_desc || "—"}</td>
                    <td className="px-4 py-4 text-right text-xs text-muted-foreground tnum">
                      {t.mode === "cash" ? "Cash" : `${t.term_months}mo · ${t.apr}% · ${t.frequency_label}`}
                      <div>{money(t.mode === "cash" ? t.cash_due : t.amount_financed)}</div>
                    </td>
                    <td className="px-4 py-4 text-right font-heading text-base money">{t.mode === "cash" ? money(t.cash_due) : money(t.payment)}</td>
                    {internal && <td className="px-4 py-4 text-right money tnum">{money(t.total_gross)}</td>}
                    <td className="px-4 py-4 text-right"><StatusPill status={r.status} tone={STATUS_TONE[r.status]} /></td>
                    <td className="px-4 py-4 text-right text-xs text-muted-foreground">{r.updated_at ? new Date(r.updated_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="new-worksheet-dialog">
          <DialogHeader>
            <DialogTitle>New worksheet</DialogTitle>
            <DialogDescription>Pick a vehicle and a customer to prefill the numbers — both are optional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <div className="label-mono mb-2">Vehicle from inventory</div>
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger data-testid="ws-vehicle-select"><SelectValue placeholder="Optional — pick a vehicle" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {inventory.map((v) => (
                    <SelectItem key={v.id} value={v.id}>
                      {v.year} {v.make} {v.model} {v.stock_number ? `· ${v.stock_number}` : ""} — {money(v.listing_price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-mono mb-2">Customer / lead</div>
              <Select value={leadId} onValueChange={setLeadId}>
                <SelectTrigger data-testid="ws-lead-select"><SelectValue placeholder="Optional — pick a lead" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}{l.phone ? ` · ${l.phone}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-mono mb-2">Worksheet name</div>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 72mo with warranty" data-testid="ws-name" />
            </div>
            <p className="text-xs text-muted-foreground">
              Nothing is locked — you can change the vehicle, price, trade and every fee on the worksheet itself. Leave both empty for a quick phone quote.
            </p>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={creating} data-testid="ws-create">{creating ? "Starting…" : "Start desking"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
